import { fiscalYearStart, fiscalYearLabel, isPartialFiscalYear } from '~/util/fiscalYear'
import { parseDateLocal } from '~/util/date'
import { format } from 'date-fns'

/** T5: the fiscal-year labels the FETCHED window leaves PARTIAL. Only the FIRST and LAST FY
 *  bucket of a window can ever be partial — consecutive whole fiscal years tile the interior
 *  exactly (isPartialFiscalYear's own "interior bucket" case) — so this never needs the actual
 *  bucketed rows, just the window bounds. Reuses fiscalYearStart/isPartialFiscalYear; no bucket
 *  arithmetic lives here.
 *
 *  FINAL-REVIEW I1 — `fromIso` must be the lower bound the walk ACTUALLY FIRED WITH: the
 *  previous-period baseline's extended start when that panel is on, `state.fromIso` otherwise
 *  (see the call site). Deliberate semantics, chosen over "the selected window":
 *
 *   - The marker exists to stop a PART year being read as a full-year accounting figure. Marking
 *     a bucket that holds a COMPLETE fiscal year is the exact inverse of that purpose.
 *   - With the baseline on, the walk runs from baselineFromIso and the split boundary is
 *     fiscalYearStart(state.fromIso), so the LEADING IN-WINDOW bucket holds a full fiscal year —
 *     yet judged against state.fromIso it was named partial. Ticking one checkbox then silently
 *     changed both the number and whether the marker was truthful.
 *   - Baseline and in-window panels come from ONE contiguous fetch, so one window governs both
 *     and both rendered edges fall out of the first/last check below — no separate baseline arm
 *     (and no way for the two arms to disagree about the same bucket).
 *
 *  F6 (recheck trigger): the baseline branch guards `!== firstStart` but not `!== lastStart`.
 *  Unreachable while shiftBackByBuckets uses subYears at fy (whole-year shifts can't land the
 *  baseline start inside the LAST fiscal year) — re-examine if that shift rule ever changes. */
export function partialFyLabels(fromIso: string, toIso: string): string[] {
  const firstStart = fiscalYearStart(parseDateLocal(fromIso))
  const lastStart = fiscalYearStart(parseDateLocal(toIso))
  const firstIso = format(firstStart, 'yyyy-MM-dd')
  const labels: string[] = []
  if (isPartialFiscalYear(firstIso, fromIso, toIso)) labels.push(fiscalYearLabel(firstStart))
  if (lastStart.getTime() !== firstStart.getTime()) {
    const lastIso = format(lastStart, 'yyyy-MM-dd')
    if (isPartialFiscalYear(lastIso, fromIso, toIso)) labels.push(fiscalYearLabel(lastStart))
  }
  return labels
}
